import { useEffect, useState, useCallback } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Loader2, Rocket, Sparkles, Users, Timer, Wand2, Crown, Trophy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { magicFeed, magicLike, magicFileUrl, magicWeeklyWinner } from "@/lib/magicApi";
import BoostModal from "@/pages/magic/BoostModal";

const CAT_COLOR = { "funny": "#FFD700", "magical": "#B47BFF", "sci-fi": "#00F0FF",
  "cool": "#FF00E5", "art": "#FF7A00", "animals": "#39FF14" };

/**
 * MagicFeed — the public wall.
 *  • Boosted creators pinned at the top with a glowing "Boosted" ribbon.
 *  • Rest is reverse-chronological, keyset-paginated (before=<created_at>).
 */
export default function MagicFeed() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);
  const [weekly, setWeekly] = useState(null);
  const { refreshStatus } = useOutletContext() || {};

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await magicFeed({ limit: 24 });
      setData(r);
      setItems(r.items || []);
      setCursor(r.has_more ? r.next_cursor : null);
    } catch { toast.error("Couldn't load the feed"); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); magicWeeklyWinner().then(setWeekly).catch(() => {}); }, [load]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await magicFeed({ limit: 24, before: cursor });
      setItems((l) => [...l, ...(r.items || [])]);
      setCursor(r.has_more ? r.next_cursor : null);
    } catch { toast.error("Couldn't load more"); }
    finally { setLoadingMore(false); }
  };

  const toggleLike = async (gen, isBoost) => {
    if (!user) { toast.error("Sign in to like"); return; }
    // Optimistic
    const upd = (g) => ({ ...g, liked_by_me: !g.liked_by_me, likes: g.likes + (g.liked_by_me ? -1 : 1) });
    if (isBoost) {
      setData((d) => ({ ...d, boosts: d.boosts.map((b) => b.generation.id === gen.id ? { ...b, generation: upd(b.generation), liked_by_me: !b.liked_by_me } : b) }));
    } else {
      setItems((l) => l.map((g) => g.id === gen.id ? upd(g) : g));
    }
    try { await magicLike(gen.id); }
    catch {
      // Revert
      if (isBoost) {
        setData((d) => ({ ...d, boosts: d.boosts.map((b) => b.generation.id === gen.id ? { ...b, generation: upd(b.generation), liked_by_me: !b.liked_by_me } : b) }));
      } else {
        setItems((l) => l.map((g) => g.id === gen.id ? upd(g) : g));
      }
      toast.error("Couldn't like");
    }
  };

  if (busy && !data) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const totalItems = items.length + (data?.boosts?.length || 0);

  return (
    <div className="space-y-5" data-testid="magic-feed">
      <header className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#FF00E5]/15 text-[#FF00E5] flex items-center justify-center">
          <Users className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Community feed</div>
          <div className="font-black text-2xl">Latest magic</div>
        </div>
        <button data-testid="feed-boost-btn" onClick={() => setBoostOpen(true)}
          className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FFD700] to-[#FF7A00] text-black font-black text-xs uppercase tracking-widest px-4 py-2 rounded-full shadow-[0_0_20px_rgba(255,215,0,0.5)]">
          <Rocket className="w-3 h-3" /> Boost me
        </button>
      </header>

      {/* Weekly Winner — sits above everything so newcomers see the prize */}
      {weekly?.winner?.generation && (
        <WeeklyWinnerCard weekly={weekly} viewerId={user?.id} />
      )}

      {/* Boosted creators */}
      {data?.boosts?.length > 0 && (
        <section data-testid="feed-boosts" className="space-y-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
            <Rocket className="w-3 h-3 text-[#FFD700]" />
            <span className="text-[#FFD700] font-bold">Boosted creators</span>
            <span className="text-white/40">· {data.boosts.length} active</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {data.boosts.map((b) => (
              <BoostCard key={b.boost_id} boost={b} onLike={() => toggleLike(b.generation, true)} viewerId={user?.id} />
            ))}
          </div>
        </section>
      )}

      {/* Fresh feed */}
      {totalItems === 0 ? (
        <div data-testid="feed-empty" className="rounded-3xl border border-[#FF00E5]/30 bg-gradient-to-br from-[#FF00E5]/15 via-[#150525] to-[#0A0512] p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#FF00E5] text-white flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7" fill="currentColor" />
          </div>
          <div className="font-black text-xl">No creations yet</div>
          <div className="text-sm text-white/60 mt-1">Be the first — pick a style and let the community see your magic.</div>
          <Link to="/magic" data-testid="feed-empty-cta"
            className="mt-5 inline-flex items-center gap-1 bg-[#FF00E5] text-white font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full">
            <Wand2 className="w-3 h-3" /> Browse styles
          </Link>
        </div>
      ) : items.length > 0 ? (
        <>
          <section data-testid="feed-latest" className="space-y-2">
            {data?.boosts?.length > 0 && (
              <div className="text-[10px] uppercase tracking-widest text-white/50">Fresh from the community</div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {items.map((g) => <FeedCard key={g.id} gen={g} onLike={() => toggleLike(g, false)} />)}
            </div>
          </section>
          {cursor && (
            <div className="text-center pt-2">
              <button data-testid="feed-load-more" onClick={loadMore} disabled={loadingMore}
                className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full disabled:opacity-40">
                {loadingMore ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</> : "Load more"}
              </button>
            </div>
          )}
        </>
      ) : null}

      {boostOpen && (
        <BoostModal onClose={() => setBoostOpen(false)}
          onBoosted={() => { setBoostOpen(false); refreshStatus?.(); load(); }}
          cost={data?.boost_cost || 500} hours={data?.boost_hours || 24} />
      )}
    </div>
  );
}

function BoostCard({ boost, onLike, viewerId }) {
  const g = boost.generation;
  const accent = CAT_COLOR[g.category] || "#FF00E5";
  const hoursLeft = Math.max(0, Math.round((new Date(boost.expires_at) - Date.now()) / 3600000));
  return (
    <article data-testid={`boost-card-${boost.boost_id}`}
      className="relative rounded-2xl overflow-hidden border-2 border-[#FFD700]/60 bg-[#150525]"
      style={{ boxShadow: "0 0 22px rgba(255,215,0,0.35)" }}>
      <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-gradient-to-r from-[#FFD700] to-[#FF7A00] text-black text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-lg">
        <Rocket className="w-2.5 h-2.5" /> Boosted
      </div>
      <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-black/70 text-[#FFD700] text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
        <Timer className="w-2.5 h-2.5" /> {hoursLeft}h left
      </div>
      <Link to={`/magic/create/${g.style_slug}`} className="block aspect-square bg-black">
        <img src={magicFileUrl(g.result_path)} alt={g.style_name} className="w-full h-full object-cover" loading="lazy" />
      </Link>
      <div className="p-2.5 bg-gradient-to-b from-black/60 to-[#150525] relative">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center font-black text-xs text-white" style={{ background: boost.avatar_color }}>
            {(boost.user_name || "?")[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-xs truncate">{boost.user_name}</div>
            <div className="text-[9px] uppercase tracking-widest truncate" style={{ color: accent }}>{g.style_name}</div>
          </div>
          <LikeButton g={g} onLike={onLike} />
        </div>
      </div>
    </article>
  );
}

function FeedCard({ gen, onLike }) {
  const accent = CAT_COLOR[gen.category] || "#FF00E5";
  return (
    <article data-testid={`feed-card-${gen.id}`}
      className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-current transition-colors bg-black group"
      style={{ color: accent }}>
      <Link to={`/magic/create/${gen.style_slug}`} className="block aspect-square">
        <img src={magicFileUrl(gen.result_path)} alt={gen.style_name} className="w-full h-full object-cover" loading="lazy" />
      </Link>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2 flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-4 h-4 rounded-full shrink-0" style={{ background: gen.user_avatar_color || "#FF00E5" }} />
            <div className="text-white font-bold text-[10px] truncate">{gen.user_name || "Anon"}</div>
          </div>
          <div className="text-white font-black text-xs leading-tight text-left truncate">{gen.style_name}</div>
        </div>
        <LikeButton g={gen} onLike={onLike} />
      </div>
    </article>
  );
}

function LikeButton({ g, onLike }) {
  return (
    <button data-testid={`like-${g.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLike(); }}
      className={"shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold transition-colors " +
        (g.liked_by_me ? "bg-[#FF00E5] text-white" : "bg-white/10 hover:bg-white/20 text-white/80")}>
      <Heart className="w-3 h-3" fill={g.liked_by_me ? "currentColor" : "none"} /> {g.likes || 0}
    </button>
  );
}

function WeeklyWinnerCard({ weekly, viewerId }) {
  const w = weekly.winner;
  const g = w.generation;
  const isMe = viewerId && viewerId === w.winner_user_id;
  return (
    <section data-testid="weekly-winner"
      className="rounded-3xl border-2 border-[#FFD700]/60 bg-gradient-to-br from-[#FFD700]/25 via-[#FF00E5]/15 to-[#0A0512] p-4 sm:p-5 relative overflow-hidden"
      style={{ boxShadow: "0 0 32px rgba(255,215,0,0.28)" }}>
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl bg-[#FFD700]/50" />
      <div className="relative flex gap-4">
        <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-2xl overflow-hidden border-2 border-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.5)]">
          <img src={magicFileUrl(g.result_path)} alt={g.style_name} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1 bg-[#FFD700] text-[#0A0512] px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest">
            <Crown className="w-2.5 h-2.5" fill="currentColor" /> Creator of the Week
          </div>
          <div className="mt-1.5 flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center font-black text-[10px] text-white" style={{ background: w.winner_avatar_color || "#FF00E5" }}>
              {(w.winner_name || "?")[0]?.toUpperCase()}
            </div>
            <div className="font-black text-lg truncate" data-testid="weekly-winner-name">{isMe ? "You" : w.winner_name || "Anon"}</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">{g.style_name}</div>
          <div className="mt-2 inline-flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1 text-[#FFD700]"><Trophy className="w-3.5 h-3.5" fill="currentColor" /> +{w.coins_awarded} coins</span>
            <span className="inline-flex items-center gap-1 text-white/70"><Heart className="w-3.5 h-3.5" fill="currentColor" /> {w.likes} likes</span>
          </div>
          {isMe && <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">🎉 Coins credited to your wallet</div>}
        </div>
      </div>
    </section>
  );
}
